import React, { useState, useContext, useCallback } from "react";
import { AccountContext } from "./../../store/AccountContext";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { v4GridContainerWidthSx } from "../../theme/v4CompatSx";
import { Form } from "react-final-form";
import { useToasts } from "react-toast-notifications";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import ReactCodeInput from "react-verification-code-input";
import ProfileLoading from "./ProfileLoading";

/**
 * PM5 T2 (money path, batch "myaccount-profile") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **AUTH ESCALATION CLASS.** Adding a phone number adds a login identifier
 * (members can log in by mobile), and the flow is gated on an SMS verification
 * code. The conversion is styling-only: no handler, validator, state value,
 * prop, condition or DOM node moved.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `background`, `myAccountContainer`,
 * `myAccountGridContainer` and `myAccountTitle` were declared but never
 * referenced from the JSX (copy-paste from MyAccount.js), so they rendered
 * nothing.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().activeBreadcrumb — a react-router <Link>, which takes a
// `className` but has no `sx` prop (guide §1.2).
const ActiveBreadcrumbLink = styled(Link)(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  "&:hover": {
    color: theme.palette.background.ffGreen,
  },
}));

// was: useStyles().verificationCodeContainer — `react-verification-code-input`
// is a third-party component that only accepts a `className` (no `sx` prop),
// so this is `styled()` rather than an `sx` constant (guide §1.2).
const VerificationCodeInput = styled(ReactCodeInput)(({ theme }) => ({
  width: "100% !important",
  fontWeight: "600",
  marginBottom: theme.spacing(2),
  "& input": {
    height: "56px !important",
    width: "15.6% !important",
    border: "1px solid #a5a5a5",
    borderRight: "none",
    fontSize: "2rem",
    fontWeight: 500,
    fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
    "&:focus": {
      border: "1px solid #aaa",
      caretColor: "#aaa",
    },
    "&:last-child": {
      borderRight: "1px solid #a5a5a5",
    },
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ v4's `<Grid container spacing={2}>` was
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaces that with `gap` on the container and no padding on the items
 * (guide §2). Carry v4's own declarations verbatim rather than re-deriving the
 * arithmetic: omit `spacing` (v9 default 0 ⇒ `gap: 0px`), and give each item
 * the 8px padding itself. Identical by construction.
 */
const v4GridContainerSx = { width: "calc(100% + 16px)", margin: "-8px" };
const v4GridItemSx = { padding: "8px" };

// was: useStyles().myAccountSubTitle. The literal `@media (min-width:600px)` /
// `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
// to the v4 values (600/960), so the emitted query is identical.
const myAccountSubTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  textAlign: "left",
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.4rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

// was: useStyles().bodyText
const bodyTextSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  "& span": {
    fontWeight: 500,
    fontSize: "1.3rem",
  },
});

// was: useStyles().bodyTextSml
const bodyTextSmlSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "0.9rem",
  "& span": {
    fontWeight: 500,
  },
});

// was: useStyles().formContainer
//
// PARITY RESTORE (guide §2, "`<Grid container>` lost its `width: 100%`"): this
// container's direct parent is another `<Grid container>`, so in v9 — where the
// Grid container emits no width rule at all — it becomes a flex item at
// `width: auto` and shrinks to fit its content. Measured on the change-email
// form: 718.66px → 245.41px.
const formContainerSx = (theme) => ({
  ...v4GridContainerWidthSx,
  // marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
});

// was: useStyles().inputField, merged with the Grid item padding above. Every
// selector here targets `react-phone-input-2`'s own class names, not `.Mui*`
// ones, so the guide §1.2b class-prefix trap does not apply.
const inputFieldItemSx = {
  padding: "8px",
  "& .react-tel-input": {
    marginTop: 0,
    marginBottom: 0,
  },
  "& .react-tel-input .flag-dropdown:before": {
    display: "none !important",
  },
  "& .react-tel-input .form-control": {
    border: "1px solid #a5a5a5 !important",
    backgroundColor: "#f7f7f7 !important",
    fontSize: "16px !important",
    borderRadius: "5px !important",
  },
};

// was: useStyles().footerDivider
const footerDividerSx = {
  height: "1px",
  backgroundColor: "#3c3b3b",
  margin: "5% 0 2% 0",
};

// was: useStyles().breadcrumbContainer
const breadcrumbContainerSx = (theme) => ({
  margin: theme.spacing(1, 0, 2, 0),
});

// was: useStyles().disabledBreadcrumb
const disabledBreadcrumbSx = { color: "#777777" };

// was: useStyles().submit, plus a v4 default that has to be restored.
//
// PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
// NOT in the v4 rule. v4's plain <Button> defaulted to `color="default"`, whose
// root style was `color: theme.palette.text.primary`. v5+ deleted `default` and
// made `primary` the default, which would paint the label lime on the lime
// `typography.button` background — an invisible button. The "Go Back" button is
// `variant="outlined"` and needs the same restore (themeV5's `outlined` slot
// only restates `color` on `:hover`).
const submitSx = (theme) => ({
  marginTop: theme.spacing(1),
  color: "text.primary",
});

// was: useStyles().validationError
const validationErrorSx = (theme) => ({ marginBottom: theme.spacing(2) });

/**
 * #773 — the profile gate.
 *
 * `AccountContext` declares `userProfile?: UserProfile` and initialises it as
 * `useState<UserProfile>()` (AccountContext.tsx:69 and :170), so it is
 * **undefined on first render** of every cold navigation to this route.
 * The form below reads `userProfile.phoneNumber` / `.countryCode` inside
 * `useState(…)` INITIALISERS, which run before any JSX is evaluated — so the
 * `{userProfile && …}` wrapper used by ChangeName.js:212 cannot save this file
 * and an early return cannot be placed ahead of its own hooks. Splitting the
 * gate into this parent is what makes the guard reachable.
 *
 * Optional chaining (`userProfile?.phoneNumber`) was the other candidate and is
 * REJECTED here on purpose — see the matching note in EditProfile.js: it stops
 * the throw but leaves the state permanently `undefined`, which would turn a
 * visible crash into a silently broken SMS verification. See ProfileLoading.tsx
 * for why the gate renders a spinner rather than nothing.
 */
export default function AddPhone() {
  const { userProfile } = useContext(AccountContext);

  if (!userProfile) {
    return <ProfileLoading />;
  }

  return <AddPhoneForm />;
}

// Historic name: this file is routed as `/myaccount/add-phone`, but its
// component was called `ChangePhone` (there is a separate ChangePhone.js).
// Renamed to match the file only because the gate above needed a distinct
// name; nothing else about it changed.
function AddPhoneForm() {
  const { addToast } = useToasts();

  // Account Context
  const {
    userProfile,
    update,
    updating,
    updateError,
    getVerificationCode,
    waitingOnVerificationCode,
    validateLogin,
    resetLoginWithPhone,
  } = useContext(AccountContext);

  // State
  const [verificationCode, setVerificationCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(userProfile.phoneNumber);
  const [countryCode, setCountryCode] = useState(userProfile.countryCode);
  const [mobileSubmitted, setMobileSubmitted] = useState(false);
  const onChange = (value, { dialCode }) => {
    setPhoneNumber(value.substring(dialCode.length));
    setCountryCode(dialCode);
  };

  const goBack = () => {
    resetLoginWithPhone();
  };

  const onSubmitMobile = useCallback(async () => {
    setMobileSubmitted(true);
    try {
      const success = await getVerificationCode({ countryCode, phoneNumber });
      if (!success) {
        throw new Error();
      }
      // addToast(
      //   "Please check your phone for the verification we have sent you",
      //   {
      //     appearance: "success"
      //   }
      // );
    } catch (e) {
      // addToast("Oops! There was an error sending the verification code", {
      //   appearance: "error"
      // });
    }
  }, [getVerificationCode, phoneNumber, countryCode]);

  // Submit Verification Code
  const onSubmitCode = useCallback(async () => {
    try {
      await update({
        countryCode: countryCode,
        phoneNumber: phoneNumber,
        phoneVerificationCode: verificationCode,
      });
      validateLogin();
      addToast("Profile updated successfully", {
        appearance: "success",
      });
    } catch (e) {}
  }, [
    validateLogin,
    addToast,
    update,
    phoneNumber,
    countryCode,
    verificationCode,
  ]);

  return (
    <Grid container>
      <Grid size={{ xs: 12, sm: 12 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbContainerSx}>
          <ActiveBreadcrumbLink to="/myaccount/overview">
            Overview
          </ActiveBreadcrumbLink>
          <Typography sx={disabledBreadcrumbSx}>Add Phone</Typography>
        </Breadcrumbs>
      </Grid>
      <Grid size={{ xs: 12, sm: 12 }}>
        <Typography variant="h2" sx={myAccountSubTitleSx}>
          Add Phone
        </Typography>
      </Grid>
      <Grid container sx={formContainerSx}>
        {!updating && (
          <>
            <Grid size={{ xs: 12, sm: 8, md: 6 }}>
              {/* {updateError && emailNameSubmitted && (
            <Alert
              severity="error"
              variant="filled"
              style={{ marginBottom: "0.5rem" }}
            >
              {updateError}
            </Alert>
          )} */}
              {!waitingOnVerificationCode && (
                <>
                  {/* NO NUMBER KNOWN. Already the operator's position — a dial
                      code with no number counts as no number — but widened from
                      `!phoneNumber` to the exact complement of the branch below
                      so the two are total. The old pair left a mirror-image dead
                      zone: a number with no dial code satisfied neither, and the
                      page rendered no form at all. Same class as the one fixed
                      in EditProfile.js; see the longer note there. */}
                  {!(userProfile.countryCode && userProfile.phoneNumber) && (
                    <>
                      <Typography
                        variant="body1"
                        sx={bodyTextSx}
                        style={{ marginBottom: "24px" }}
                      >
                        Provide your new mobile phone number below.
                      </Typography>
                      <Form
                        onSubmit={onSubmitMobile}
                        initialValues={{
                          countryCode: userProfile.countryCode,
                          phoneNumber: userProfile.phoneNumber,
                        }}
                      >
                        {(props) => (
                          <form onSubmit={props.handleSubmit}>
                            <Grid container sx={v4GridContainerSx}>
                              <Grid
                                size={{ xs: 12, sm: 12 }}
                                sx={inputFieldItemSx}
                              >
                                {updateError && mobileSubmitted && (
                                  <Alert severity="error" variant="filled">
                                    {updateError}
                                  </Alert>
                                )}
                                <PhoneInput
                                  country="gb"
                                  inputProps={{
                                    name: "mobileNumber",
                                    required: true,
                                    autoFocus: false,
                                  }}
                                  placeholder="Mobile Number"
                                  aria-label="Mobile Number"
                                  preferredCountries={[
                                    "gb",
                                    "ie",
                                    "us",
                                    "au",
                                    "nz",
                                  ]}
                                  excludeCountries={["je"]}
                                  enableTerritories={true}
                                  onChange={onChange}
                                  disabled={updating}
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                                <Button
                                  sx={submitSx}
                                  type="submit"
                                  disabled={updating}
                                >
                                  Add Mobile
                                </Button>
                              </Grid>
                            </Grid>
                          </form>
                        )}
                      </Form>
                    </>
                  )}
                  {userProfile.countryCode && userProfile.phoneNumber && (
                    <>
                      <Typography
                        variant="body1"
                        sx={bodyTextSx}
                        style={{ marginBottom: "24px" }}
                      >
                        Provide your new mobile phone number below.
                      </Typography>
                      <Form
                        onSubmit={onSubmitMobile}
                        initialValues={{
                          countryCode: userProfile.countryCode,
                          phoneNumber: userProfile.phoneNumber,
                        }}
                      >
                        {(props) => (
                          <form onSubmit={props.handleSubmit}>
                            <Grid container sx={v4GridContainerSx}>
                              <Grid
                                size={{ xs: 12, sm: 12 }}
                                sx={inputFieldItemSx}
                              >
                                {updateError && mobileSubmitted && (
                                  <Alert severity="error" variant="filled">
                                    {updateError}
                                  </Alert>
                                )}
                                <PhoneInput
                                  inputProps={{
                                    name: "mobileNumber",
                                    required: true,
                                    autoFocus: false,
                                  }}
                                  placeholder="Mobile Number"
                                  aria-label="Mobile Number"
                                  preferredCountries={[
                                    "gb",
                                    "ie",
                                    "us",
                                    "au",
                                    "nz",
                                  ]}
                                  excludeCountries={["je"]}
                                  enableTerritories={true}
                                  onChange={onChange}
                                  disabled={updating}
                                  value={
                                    userProfile.countryCode +
                                    userProfile.phoneNumber
                                  }
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                                <Button
                                  sx={submitSx}
                                  type="submit"
                                  disabled={updating}
                                >
                                  Update Mobile
                                </Button>
                              </Grid>
                            </Grid>
                          </form>
                        )}
                      </Form>
                    </>
                  )}
                </>
              )}

              {waitingOnVerificationCode && (
                <>
                  <Typography
                    variant="body1"
                    sx={bodyTextSx}
                    align="left"
                    style={{ marginBottom: "24px" }}
                  >
                    Please enter the verification code sent to:
                    <br />
                    <br />
                    <span>
                      +{countryCode}
                      {phoneNumber}
                    </span>
                    .
                  </Typography>
                  <Grid size={{ xs: 12, sm: 12 }}>
                    {updateError && mobileSubmitted && (
                      <Alert
                        severity="error"
                        variant="filled"
                        sx={validationErrorSx}
                      >
                        {updateError}
                      </Alert>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12, sm: 12 }}>
                    <VerificationCodeInput
                      type="number"
                      fields={6}
                      value={verificationCode}
                      onComplete={(e) => setVerificationCode(e)}
                      disabled={updating}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12 }}>
                    <Button size="medium" sx={submitSx} onClick={onSubmitCode}>
                      Verify Phone
                    </Button>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12 }}>
                    <Button
                      size="medium"
                      variant="outlined"
                      style={{ marginTop: "16px" }}
                      sx={submitSx}
                      onClick={goBack}
                    >
                      Go Back
                    </Button>
                  </Grid>
                </>
              )}
              <Divider sx={footerDividerSx} />
              <Typography variant="body1" sx={bodyTextSmlSx}>
                <span>Tip</span> - did you know you can log in with your Mobile
                Number?
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}></Grid>
          </>
        )}
        {updating && (
          <Box sx={{ m: 5 }} align="center">
            <CircularProgress variant="indeterminate"></CircularProgress>
            <Typography variant="body2" sx={bodyTextSmlSx}>
              <br />
              Updating your Phone Number. Please do not refresh your browser.
            </Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
